"use client"
import PropTypes from 'prop-types';
import { signOut, signInWithRedirect, updateUserAttribute, fetchUserAttributes } from 'aws-amplify/auth';
import { useMemo, useState, useEffect, useContext, useCallback, createContext } from 'react';
import { useSearchParams, useRouter } from "next/navigation";

import * as PolnApi from "../../utils/api-service";
import PageLoader from "../../components/Loader/pageLoader";
import { usePostHog } from 'posthog-js/react';
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

async function defaultRoute(navigate, searchParams) {
    let redirectUrl = '/deals/creator';

    const inviteKey = searchParams.get('inviteKey');
    if (inviteKey) redirectUrl += `?inviteKey=${inviteKey}`

    navigate.push(redirectUrl);
}

async function doSignInWithRedirect(searchParams) {
    const customStates = {};
    customStates.inviteKey = searchParams.get('inviteKey');
    await signInWithRedirect({ provider: 'Google', customState: JSON.stringify(customStates) });
}

export const AuthProvider = ({ children }) => {

    const searchParams = useSearchParams()
    const navigate = useRouter()
    const postHog = usePostHog()

    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [user, setUser] = useState(null);
    const [layoutLoading, setLayoutLoader] = useState(true);
    const [isNewUser, setIsNewUser] = useState(false);

    const getUser = useCallback(async () => {
        try {
            if (user) {
                return user
            }

            const userAttr = await fetchUserAttributes();

            if (userAttr) {
                let data;
                try {
                    let response = await PolnApi.request(`/poln/user`, { notify: false, skipThrow: true });

                    data = response?.body.data;

                    if (!data) {
                        // User is signing up.
                        setUser({ ...userAttr, setupInProgress: true });
                        return;
                    }

                    // user has already signed up
                    const usr = { ...userAttr, ...data };
                    if (!postHog._isIdentified()) {
                        postHog.identify(usr.email, {
                            email: usr.email,
                            cognitoId: usr.cognitoId,
                            name: usr.name
                        })
                    }
                    setUser(usr);
                    setIsAuthenticated(Boolean(data.cognitoId));
                } catch (e) {
                    console.log('Error: ', e.message);
                }
            } else if (searchParams && searchParams.size && searchParams.get('code')) {
                // perhaps user is coming from idp, lets submit again
                await doSignInWithRedirect(searchParams)
            } else {
                defaultRoute(navigate, searchParams)
            }
        } catch (e) {
            if (e?.name !== 'UserUnAuthenticatedException') {
                console.log('Error fetching user data: ', e);
                // TODO: Adeel to add bugsnag raise
            }
            defaultRoute(navigate, searchParams)
        } finally {
            setLayoutLoader(false);
        }
    }, []);

    useEffect(() => {
        getUser();
    }, [getUser]);

    const federationLogin = useCallback(async () => {
        try {
            await doSignInWithRedirect(searchParams)
        } catch (e) {
            console.log('Error: ', e.message);
        }
    }, []);

    const logout = useCallback(async () => {
        await signOut();
    }, []);

    const markUserAsExisting = useCallback(() => {
        setIsNewUser(false);
    }, []);

    const setDDTour = (value) => {
        updateUserAttribute({
            userAttribute: {
                attributeKey: 'custom:dd_tour',
                value
            }
        });
    }

    const authContextValue = useMemo(() => ({
        isAuthenticated,
        federationLogin,
        logout,
        user,
        layoutLoading,
        setLayoutLoader,
        isNewUser,
        markUserAsExisting,
        setDDTour,
        setUser
    }), [isAuthenticated, federationLogin, logout, user, isNewUser, markUserAsExisting, setDDTour, layoutLoading, setLayoutLoader, setUser]);

    if (layoutLoading) return <PageLoader />;

    return (
        <AuthContext.Provider value={authContextValue}>
            {children}
        </AuthContext.Provider>
    );
};

AuthProvider.propTypes = {
    children: PropTypes.node.isRequired
}
